import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Layout } from "@/components/Layout";
import { setAuthTokenGetter } from "@/lib/hiring-api";
import { supabase } from "@/integrations/supabase/client";
import Login from "./pages/Login";

// Wire Supabase session token into every hiring API call via Authorization: Bearer <token>
setAuthTokenGetter(async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
});
import Dashboard from "./pages/Dashboard";
import HiddenGems from "./pages/HiddenGems";

// Hiring Tool pages
import HiringDashboard from "./pages/hiring/campaigns/index";
import HiringNewCampaign from "./pages/hiring/campaigns/new";
import HiringEditCampaign from "./pages/hiring/campaigns/edit";
import HiringCampaignDetail from "./pages/hiring/campaigns/detail";
import HiringFindCreators from "./pages/hiring/campaigns/find";
import AcceptOffer from "./pages/hiring/accept";
import PublicLeaderboard from "./pages/hiring/leaderboard";
import { HiringThemeWrapper } from "./components/hiring/HiringThemeWrapper";

import Challenges from "./pages/Challenges";
import Campaigns from "./pages/Campaigns";

import UsersPage from "./pages/Users";
import Submissions from "./pages/Submissions";
import Notifications from "./pages/Notifications";
import Feedback from "./pages/Feedback";
import TicketChat from "./pages/TicketChat";
import Analytics from "./pages/Analytics";
import Revenue from "./pages/Revenue";
import TAPData from "./pages/TAPData";
import CAPData from "./pages/CAPData";
import WeeklyTop100 from "./pages/WeeklyTop100";
import BrandIntelligence from "./pages/BrandIntelligence";
import BriefRequests from "./pages/BriefRequests";
import AuditLog from "./pages/AuditLog";
import TrybeOutreach from "./pages/TrybeOutreach";
import BrandActivity from "./pages/BrandActivity";
import CAPApplications from "./pages/CAPApplications";
import RosterCampaigns from "./pages/RosterCampaigns";
import RosterCampaignDetail from "./pages/RosterCampaignDetail";
import CampaignOps from "./pages/CampaignOps";
import CreatorPayments from "./pages/CreatorPayments";
import PayoutBatches from "./pages/PayoutBatches";
import AccountsFinance from "./pages/AccountsFinance";
import HireDetail from "./pages/HireDetail";
import RosterCreatorDetail from "./pages/RosterCreatorDetail";
import SampleRequests from "./pages/SampleRequests";
import HybridLedger from "./pages/HybridLedger";
import BrandCredits from "./pages/BrandCredits";
import LinkGenerator from "./pages/LinkGenerator";
import CoachGuide from "./pages/CoachGuide";
import NotFound from "./pages/NotFound";
import DebugSentry from "./pages/DebugSentry";

const queryClient = new QueryClient();

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}
// Staging build is configured with branch "common_branch_refined"

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/hidden-gems" element={<ProtectedRoute><HiddenGems /></ProtectedRoute>} />
            
            <Route path="/challenges" element={<ProtectedRoute><Challenges /></ProtectedRoute>} />
            <Route path="/campaigns" element={<ProtectedRoute><Campaigns /></ProtectedRoute>} />
            <Route path="/leaderboard" element={<Navigate to="/cap-data" replace />} />
            <Route path="/users" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
            <Route path="/submissions" element={<ProtectedRoute><Submissions /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
            <Route path="/feedback" element={<ProtectedRoute><Feedback /></ProtectedRoute>} />
            <Route path="/ticket-chat/:id" element={<ProtectedRoute><TicketChat /></ProtectedRoute>} />
            <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
            <Route path="/revenue" element={<ProtectedRoute><Revenue /></ProtectedRoute>} />
            <Route path="/tap-data" element={<ProtectedRoute><TAPData /></ProtectedRoute>} />
            <Route path="/cap-data" element={<ProtectedRoute><CAPData /></ProtectedRoute>} />
            <Route path="/weekly-top-100" element={<ProtectedRoute><WeeklyTop100 /></ProtectedRoute>} />
            <Route path="/brand-intelligence" element={<ProtectedRoute><BrandIntelligence /></ProtectedRoute>} />
            <Route path="/brief-requests" element={<ProtectedRoute><BriefRequests /></ProtectedRoute>} />
            <Route path="/audit-log" element={<ProtectedRoute><AuditLog /></ProtectedRoute>} />
            <Route path="/trybe-outreach" element={<ProtectedRoute><TrybeOutreach /></ProtectedRoute>} />
            <Route path="/brand-activity" element={<ProtectedRoute><BrandActivity /></ProtectedRoute>} />
            <Route path="/cap-applications" element={<ProtectedRoute><CAPApplications /></ProtectedRoute>} />
            <Route path="/roster-campaigns" element={<ProtectedRoute><RosterCampaigns /></ProtectedRoute>} />
            <Route path="/roster-campaigns/:id" element={<ProtectedRoute><RosterCampaignDetail /></ProtectedRoute>} />
            <Route path="/campaign-ops" element={<ProtectedRoute><CampaignOps /></ProtectedRoute>} />
            <Route path="/sample-requests" element={<ProtectedRoute><SampleRequests /></ProtectedRoute>} />
            <Route path="/creator-payments" element={<ProtectedRoute><CreatorPayments /></ProtectedRoute>} />
            <Route path="/creator-payments/batches" element={<ProtectedRoute><PayoutBatches /></ProtectedRoute>} />
            <Route path="/creator-payments/hires/:id" element={<ProtectedRoute><HireDetail /></ProtectedRoute>} />
            <Route path="/accounts-finance" element={<ProtectedRoute><AccountsFinance /></ProtectedRoute>} />
            <Route path="/roster-creators/:id" element={<ProtectedRoute><RosterCreatorDetail /></ProtectedRoute>} />
            <Route path="/hybrid-ledger" element={<ProtectedRoute><HybridLedger /></ProtectedRoute>} />
            <Route path="/brand-credits" element={<ProtectedRoute><BrandCredits /></ProtectedRoute>} />
            <Route path="/link-generator" element={<ProtectedRoute><LinkGenerator /></ProtectedRoute>} />
            <Route path="/coach-guide" element={<ProtectedRoute><CoachGuide /></ProtectedRoute>} />

            {/* Hiring Tool — protected routes (wrapped in pink theme) */}
            <Route path="/hiring" element={<ProtectedRoute><HiringThemeWrapper><HiringDashboard /></HiringThemeWrapper></ProtectedRoute>} />
            <Route path="/hiring/campaigns/new" element={<ProtectedRoute><HiringThemeWrapper><HiringNewCampaign /></HiringThemeWrapper></ProtectedRoute>} />
            <Route path="/hiring/campaigns/:id/edit" element={<ProtectedRoute><HiringThemeWrapper><HiringEditCampaign /></HiringThemeWrapper></ProtectedRoute>} />
            <Route path="/hiring/campaigns/:id/find" element={<ProtectedRoute><HiringThemeWrapper><HiringFindCreators /></HiringThemeWrapper></ProtectedRoute>} />
            <Route path="/hiring/campaigns/:id" element={<ProtectedRoute><HiringThemeWrapper><HiringCampaignDetail /></HiringThemeWrapper></ProtectedRoute>} />

            {/* Hiring Tool — public routes (full-page, no auth, accessed by creators via email links) */}
            <Route path="/accept/:token" element={<HiringThemeWrapper fullPage><AcceptOffer /></HiringThemeWrapper>} />
            <Route path="/leaderboard/:token" element={<HiringThemeWrapper fullPage><PublicLeaderboard /></HiringThemeWrapper>} />

            {/* Sentry test route — dev/staging only, returns 404 in production. */}
            <Route path="/debug-sentry" element={<DebugSentry />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
